/**
 * Spans of time as people read them in bookings and plans: „7,5 h“,
 * „1:30 h“, „1 h 30 min“, „1 Std. 30 Min.“. For a running stopwatch
 * („1:02:03“) use `formatElapsed` (Timer).
 */
import { toFiniteNumber, type NxNumberLike } from './number';
import { numberFormat } from './intlCache';

export type NxDurationUnit = 'ms' | 'seconds' | 'minutes' | 'hours';

/**
 * - `clock` „1:30“
 * - `hm` „1:30 h“
 * - `short` „1 h 30 min“ (unit symbols, the same in every language)
 * - `long` „1 Std. 30 Min.“ (the locale's own unit names)
 */
export type NxDurationStyle = 'clock' | 'hm' | 'short' | 'long';

export interface NxFormatHoursOptions {
  /** What the value counts. Defaults to `hours`. */
  unit?: NxDurationUnit;
  /** At most this many decimals. Defaults to 1. */
  digits?: number;
  /** A `+` on positive values, e.g. for an overrun („+1,5 h“). */
  signed?: boolean;
  /** Append „ h“. Defaults to `true`; `false` gives the bare number. */
  suffix?: boolean;
  locale?: string;
  fallback?: string;
}

export interface NxFormatDurationOptions {
  /** What the value counts. Defaults to `minutes`. */
  unit?: NxDurationUnit;
  /** Defaults to `short`. */
  style?: NxDurationStyle;
  locale?: string;
  fallback?: string;
}

const TO_MINUTES: Record<NxDurationUnit, number> = {
  ms: 1 / 60_000,
  seconds: 1 / 60,
  minutes: 1,
  hours: 60,
};

/**
 * A duration as decimal hours: `7.5` → „7,5 h“, or from minutes with
 * `{ unit: 'minutes' }` (`450` → „7,5 h“).
 */
export function formatHours(value: NxNumberLike, options: NxFormatHoursOptions = {}): string {
  const n = toFiniteNumber(value);
  if (n === null) return options.fallback ?? '';
  const hours = (n * TO_MINUTES[options.unit ?? 'hours']) / 60;
  const text = numberFormat(options.locale, {
    maximumFractionDigits: options.digits ?? 1,
    ...(options.signed ? { signDisplay: 'exceptZero' } : {}),
  }).format(hours);
  return options.suffix === false ? text : `${text} h`;
}

function unitText(
  locale: string | undefined,
  value: number,
  unit: 'hour' | 'minute' | 'second',
  long: boolean
): string {
  if (!long) {
    const symbol = unit === 'hour' ? 'h' : unit === 'minute' ? 'min' : 's';
    return `${numberFormat(locale, { maximumFractionDigits: 0 }).format(value)} ${symbol}`;
  }
  return numberFormat(locale, { style: 'unit', unit, unitDisplay: 'short' }).format(value);
}

/**
 * A duration in hours and minutes, rounded to the minute. Under a minute
 * (from seconds or ms) the `short` and `long` styles show seconds instead of
 * „0 min“. Negative spans keep their sign („-0:15“).
 */
export function formatDuration(value: NxNumberLike, options: NxFormatDurationOptions = {}): string {
  const n = toFiniteNumber(value);
  if (n === null) return options.fallback ?? '';
  const style = options.style ?? 'short';
  const minutesExact = n * TO_MINUTES[options.unit ?? 'minutes'];
  const sign = minutesExact < 0 ? '-' : '';
  const absMinutes = Math.abs(minutesExact);
  const total = Math.round(absMinutes);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;

  if (style === 'clock' || style === 'hm') {
    const clock = `${sign}${hours}:${String(minutes).padStart(2, '0')}`;
    return style === 'hm' ? `${clock} h` : clock;
  }

  const long = style === 'long';
  const seconds = Math.round(absMinutes * 60);
  if (options.unit && options.unit !== 'minutes' && options.unit !== 'hours' && seconds < 60) {
    return seconds === 0
      ? unitText(options.locale, 0, 'minute', long)
      : sign + unitText(options.locale, seconds, 'second', long);
  }
  if (total === 0) return unitText(options.locale, 0, 'minute', long);
  const parts: string[] = [];
  if (hours > 0) parts.push(unitText(options.locale, hours, 'hour', long));
  if (minutes > 0) parts.push(unitText(options.locale, minutes, 'minute', long));
  return sign + parts.join(' ');
}
